/**
 * SpecCardForm — форма создания спецверсии (.cform, эталон renderCreateS
 * js:417-437) и expanded-тело карточки (specBody js:438-475): Название →
 * cfg-grid [Базовый квест (Dropdown single с «— не привязана» — QS-1: сервер
 * базу не требует) | Доступна клиентам (ctog grn + pubnote) | Игра·Пауза
 * span-2 | Операторы + пустая ячейка] → Расходники (ConsChips, нормы
 * version_id) → priceGrid three → helper live-Δ → ActionPair. Первая
 * привязка — prefill с базового (prefillSpec js:47, 446): состав и цены
 * сразу, нормы — fetch'ем, не затирая начатую правку.
 */
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import type { Quest, QuestVersion } from "../../api/types";
import { Dropdown } from "../../components/shared";
import type { ConsItem } from "../../hooks/useQuestsAdmin";
import type { SpecsAdmin } from "../../hooks/useSpecsAdmin";
import { ConsChips } from "./ConsChips";
import { crewToOps, OpsRow, opsOfQuest, opsToCrew, type OpG } from "./OpsRow";
import { PriceRow } from "./QuestCells";
import { ActionPair, CollSec, DualCell, fmtD } from "./QuestFormCells";
import cfg from "./config.module.css";
import css from "./quests.module.css";

/** Драфт expanded-формы спецверсии (эталон sfdraft js:438). cons: null = нормы не трогали. */
export interface SpecFormDraft {
  id: string; name: string; base: string | null; dur: number; reset: number;
  ops: OpG[]; cons: ConsItem[] | null; pn: number; pb: number; pp: number; pub: boolean;
}

export function initSpecDraft(v: QuestVersion): SpecFormDraft {
  return {
    id: v.id, name: v.title, base: v.slot_quest_id,
    dur: v.duration_min ?? v.base_duration ?? 60,
    reset: v.reset_min ?? v.base_reset ?? 15,
    ops: crewToOps(v.operators_comp), cons: null,
    pn: v.base_players ?? 4, pb: v.base_price ?? 0, pp: v.price_per_extra ?? 0,
    pub: !!v.client_visible,
  };
}

/** Ячейка «Базовый квест»: te-numbtn → Dropdown single (эталон js:443-447). */
function BaseCell({ base, quests, onPick }: {
  base: string | null;
  quests: Quest[];
  onPick: (id: string | null) => void;
}) {
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const bq = base ? quests.find((x) => x.id === base) : undefined;
  return (
    <div className={`${cfg.cell} ${css.cellInline}`}>
      <span className={cfg.klbl}>Базовый квест</span>
      <button type="button" className={cfg.teNumbtn} onClick={(e) => setDd(e.currentTarget)}>
        {bq ? bq.short_name : <span className={cfg.dash}>—</span>}
      </button>
      <Dropdown
        open={!!dd}
        anchor={dd}
        multi={false}
        title="Базовый квест"
        items={[
          { id: "", label: "— не привязана" },
          ...quests.map((x) => ({ id: x.id, label: x.name, meta: x.short_name })),
        ]}
        selectedIds={base ? [base] : []} /* js:446 — без базы ничего не подсвечено */
        onCommit={(ids) => { const id = String(ids[0] ?? ""); onPick(id || null); }}
        onClose={() => setDd(null)}
      />
    </div>
  );
}

export interface SpecCardBodyProps {
  v: QuestVersion;
  sa: SpecsAdmin;
  draft: SpecFormDraft;
  setDraft: Dispatch<SetStateAction<SpecFormDraft | null>>;
  onCancel: () => void;
  secOpen: Record<string, boolean>;
  onSec: (key: string) => void;
}

export function SpecCardBody({ v, sa, draft, setDraft, onCancel, secOpen, onSec }: SpecCardBodyProps) {
  const { loadVersionCons, questConsFor } = sa;
  useEffect(() => { loadVersionCons(v.id); }, [loadVersionCons, v.id]);
  /* «Перенести в нормальные квесты» (21.08, Саша) — двухшаговое подтверждение */
  const [convArm, setConvArm] = useState(false);

  const serverCons = useMemo<ConsItem[]>(
    () => (sa.consVer[v.id] ?? []).map((r) => ({ consumable_id: r.consumable_id, qty: r.qty, per_player: r.per_player })),
    [sa.consVer, v.id],
  );
  const cons = draft.cons ?? serverCons;
  const upd = (p: Partial<SpecFormDraft>) => setDraft((f) => (f ? { ...f, ...p } : f));
  const K = `${v.id}:scons`;
  const bq = draft.base ? sa.quests.find((x) => x.id === draft.base) : undefined;

  /* Первая привязка — prefill с базового (js:446): состав/цены сразу,
     нормы — fetch (применяем, только если cons ещё не начали править). */
  const pickBase = (id: string | null) => {
    if (!id || draft.base) { upd({ base: id }); return; }
    const q = sa.quests.find((x) => x.id === id);
    const p = sa.prices[id];
    upd({
      base: id,
      ops: q ? opsOfQuest(q) : draft.ops,
      pn: p?.threshold ?? draft.pn,
      pb: p?.base_price ?? draft.pb,
      pp: p?.extra_per_player ?? draft.pp,
    });
    void questConsFor(id).then((items) => setDraft((f) =>
      (f && f.id === v.id && f.base === id && f.cons === null ? { ...f, cons: items } : f)));
  };

  const save = () => {
    void sa.saveSpec(v, {
      title: draft.name.trim() || v.title, slot_quest_id: draft.base,
      duration_min: draft.dur, reset_min: draft.reset, crew: opsToCrew(draft.ops),
      client_visible: draft.pub, cons: draft.cons,
      pn: draft.pn, pb: draft.pb, pp: draft.pp,
    });
  };

  return (
    <div className={css.qbody} onClick={(e) => e.stopPropagation()}>
      <div className={css.gap2}>
        <span className={cfg.klbl}>Название</span>
        <input className={css.txt} value={draft.name} onChange={(e) => upd({ name: e.target.value })} />
      </div>
      <div className={`${cfg.cfgGrid} ${css.gridTop} ${css.gridBot}`}>
        <BaseCell base={draft.base} quests={sa.quests} onPick={pickBase} />
        <div className={cfg.cell}>
          <span className={cfg.klbl}>Доступна клиентам</span>
          <button
            type="button"
            className={`${cfg.ctog} ${cfg.grn} ${draft.pub ? cfg.on : ""}`}
            style={{ alignSelf: "flex-start" }}
            aria-pressed={draft.pub}
            onClick={() => upd({ pub: !draft.pub })}
          >
            <span className={cfg.ttrack} />
            <span className={cfg.tstate}>{draft.pub ? "Да" : "Нет"}</span>
          </button>
          {draft.pub ? <div className={css.pubnote}>видна в виджете и Unifans</div> : null}
        </div>
        <DualCell span2 dur={draft.dur} reset={draft.reset} onDur={(x) => upd({ dur: x })} onReset={(x) => upd({ reset: x })} />
        <OpsRow ops={draft.ops} onChange={(o) => upd({ ops: o })} />
        <div className={cfg.cell} />
      </div>
      <CollSec title="Расходники" count={cons.length} open={!!secOpen[K]} onToggle={() => onSec(K)}>
        <ConsChips
          items={cons}
          cat={sa.consCat}
          onChange={(list) => upd({ cons: list })}
          onAdd={(id, qty) => setDraft((f) => (f
            ? { ...f, cons: [...(f.cons ?? serverCons), { consumable_id: id, qty, per_player: 1 }] }
            : f))}
        />
      </CollSec>
      <PriceRow
        three
        pn={draft.pn} pb={draft.pb} pp={draft.pp}
        onPn={(x) => upd({ pn: x })} onPb={(x) => upd({ pb: x })} onPp={(x) => upd({ pp: x })}
      />
      <div className={cfg.helper} style={{ padding: "2px 0 0" }}>
        {bq ? (
          <>
            Сетка — базового квеста «{bq.name}». При брони день сдвигается на{" "}
            <span className={css.num} style={{ color: "var(--cyan)" }}>{fmtD(draft.dur - bq.duration)}м</span>
          </>
        ) : (
          <span style={{ color: "var(--orange)" }}>Не привязана к квесту — сетки нет, бронирование недоступно.</span>
        )}
      </div>
      <div className={cfg.helper} style={{ padding: "4px 0 0" }}>
        {draft.base ? (
          <button
            type="button"
            className={css.convBtn}
            style={convArm ? { color: "var(--orange)", borderColor: "var(--orange)" } : undefined}
            onClick={() => { if (!convArm) { setConvArm(true); return; } setConvArm(false); sa.convertSpec(v); }}
          >
            {convArm ? "Точно перенести? Спецверсия станет обычным квестом площадки и уйдёт из этого списка" : "⇄ Перенести в нормальные квесты"}
          </button>
        ) : (
          <span style={{ color: "var(--text-3)" }}>Перенос в нормальные квесты доступен после привязки базового квеста — площадка и сетка берутся у него.</span>
        )}
      </div>
      <ActionPair okLabel="Сохранить" onOk={save} onCancel={onCancel} />
    </div>
  );
}

export interface SpecCreateDraft { name: string; dur: number; reset: number; ops: OpG[] }

export function SpecCreateForm({ f, setF, busy, onCreate, onCancel }: {
  f: SpecCreateDraft;
  setF: Dispatch<SetStateAction<SpecCreateDraft>>;
  busy: boolean;
  onCreate: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={css.cform}>
      <div className={cfg.secRow}>
        <h3 className={`${cfg.secT} ${css.cformT}`}>Новая спецверсия</h3>
      </div>
      <div className={css.gap2}>
        <span className={cfg.klbl}>Название *</span>
        <input
          className={css.txt} placeholder="Хоррор-версия" value={f.name} autoFocus
          onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))}
        />
      </div>
      <div className={cfg.cfgGrid}>
        <DualCell
          dur={f.dur} reset={f.reset}
          onDur={(x) => setF((p) => ({ ...p, dur: x }))}
          onReset={(x) => setF((p) => ({ ...p, reset: x }))}
        />
        <OpsRow ops={f.ops} onChange={(o) => setF((p) => ({ ...p, ops: o }))} />
      </div>
      <ActionPair
        okLabel={busy ? "Создаём…" : "Создать"}
        okDisabled={busy || !f.name.trim()}
        onOk={onCreate}
        onCancel={onCancel}
      />
    </div>
  );
}
